import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowUUpLeft } from "@phosphor-icons/react";
import { fmtINR } from "@/lib/format";

export function ReversePaymentButton({ payment, onReversed, variant = "icon" }) {
  const { user } = useAuth();
  const canReverse = ["super_admin", "client_admin"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  if (!canReverse) return null;

  const submit = async () => {
    if (!pin) return toast.error("Enter your confirmation PIN");
    setBusy(true);
    try {
      await api.post(`/payments/${payment.id}/reverse`, { pin });
      toast.success(`Reversed ${fmtINR(payment.amount)} — balance restored`);
      setOpen(false);
      setPin("");
      onReversed && onReversed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reverse payment");
    } finally { setBusy(false); }
  };

  return (
    <>
      {variant === "icon" ? (
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          title="Reverse / delete this payment"
          className="h-8 w-8 inline-flex items-center justify-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors"
          data-testid={`reverse-payment-${payment.id}`}
        >
          <ArrowUUpLeft size={16} weight="bold" />
        </button>
      ) : (
        <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-8 text-destructive border-destructive/30 hover:bg-destructive/10"
          onClick={() => setOpen(true)} data-testid={`reverse-payment-${payment.id}`}>
          <ArrowUUpLeft size={14} weight="bold" /> Reverse
        </Button>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPin(""); }}>
        <DialogContent className="rounded-sm" data-testid="reverse-payment-dialog">
          <DialogHeader>
            <DialogTitle>Reverse payment?</DialogTitle>
            <DialogDescription>
              This permanently deletes the payment record and <b>adds {fmtINR(payment.amount)} back to the outstanding balance</b>, removes it from Recovered, and re-opens the account if it was auto-closed. The reversal is logged in the Audit Log.
            </DialogDescription>
          </DialogHeader>
          <div className="border border-border bg-secondary/40 p-3 rounded-sm text-xs space-y-1">
            <div><span className="label-caps">Amount</span> · <span className="mono font-semibold">{fmtINR(payment.amount)}</span> ({payment.mode})</div>
            {payment.loan_id && <div><span className="label-caps">Account</span> · {payment.loan_id} {payment.borrower_name ? `— ${payment.borrower_name}` : ""}</div>}
            {payment.reference && <div className="text-muted-foreground">Ref: {payment.reference}</div>}
          </div>
          <div>
            <Label className="text-xs">Confirmation PIN</Label>
            <Input
              type="password" inputMode="numeric" maxLength={4}
              placeholder="4-digit PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              className="rounded-sm w-40"
              data-testid="reverse-payment-pin"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} data-testid="cancel-reverse-payment">Cancel</Button>
            <Button variant="destructive" className="rounded-sm" onClick={submit} disabled={busy} data-testid="confirm-reverse-payment">
              {busy ? "Reversing…" : "Reverse payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
